// Asset type icons and labels
export const ASSET_ICONS = {
  cell_phone: '📱',
  tablet: '📟',
  radio: '📻',
  laptop: '💻',
  desktop: '🖥️',
  server: '🖧',
  software_license: '🔑',
};

export const ASSET_LABELS = {
  cell_phone: 'Cell Phone',
  tablet: 'Tablet',
  radio: 'Radio',
  laptop: 'Laptop',
  desktop: 'Desktop',
  server: 'Server',
  software_license: 'Software License',
};

export const ASSET_TYPES = Object.keys(ASSET_LABELS);

export const STATUSES = [
  { value: 'active',          label: 'Active',          badge: 'badge-green' },
  { value: 'in_storage',      label: 'In Storage',      badge: 'badge-gray' },
  { value: 'in_repair',       label: 'In Repair',       badge: 'badge-yellow' },
  { value: 'pending_return',  label: 'Pending Return',  badge: 'badge-orange' },
  { value: 'lost',            label: 'Lost',            badge: 'badge-red' },
  { value: 'stolen',          label: 'Stolen',          badge: 'badge-red' },
  { value: 'retired',         label: 'Retired',         badge: 'badge-gray' },
];

export const DAMAGE_TYPES = [
  { value: 'screen',   label: 'Screen Damage' },
  { value: 'water',    label: 'Water Damage' },
  { value: 'physical', label: 'Physical Damage' },
  { value: 'battery',  label: 'Battery Issue' },
  { value: 'lost',     label: 'Lost' },
  { value: 'stolen',   label: 'Stolen' },
  { value: 'other',    label: 'Other' },
];

// How a retired asset was disposed of. The API defaults to 'e-waste' and does
// not validate this in code, so any CHECK constraint lives in the database
// itself — a value the DB rejects surfaces as a failed retire, not a bad write.
export const RETIREMENT_METHODS = [
  { value: 'e-waste',            label: 'E-Waste / Recycled' },
  { value: 'returned-to-vendor', label: 'Returned to Vendor' },
  { value: 'trade-in',           label: 'Trade-In' },
  { value: 'sold',               label: 'Sold' },
  { value: 'donated',            label: 'Donated' },
  { value: 'destroyed',          label: 'Destroyed' },
  { value: 'lost',               label: 'Lost' },
  { value: 'stolen',             label: 'Stolen' },
];

export function statusBadge(status) {
  const s = STATUSES.find(x => x.value === status);
  return s ? { className: s.badge, label: s.label } : { className: 'badge-gray', label: status };
}

export function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

export function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

// Compact relative age, e.g. "3h ago". Pairs with formatDateTime so a
// timestamp can show both the exact moment and how stale it is.
export function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const then = new Date(dateStr).getTime();
  if (!Number.isFinite(then)) return 'unknown';

  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 0) return 'just now';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  return `${Math.floor(months / 12)}y ago`;
}

// Counts arrive from Postgres COUNT(*) as strings, so `count !== 1` is true
// even for a single row and renders "1 devices". Four call sites disagreed on
// whether to compare against 1 or '1'; this compares numerically, once.
export function plural(count, singular, pluralForm) {
  return Number(count) === 1 ? singular : (pluralForm ?? singular + 's');
}

export function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export function roleLabel(role) {
  const map = {
    it_admin: 'IT Admin',
    it_staff: 'IT Staff',
    supervisor: 'Supervisor',
    hr: 'HR',
    read_only: 'Read Only',
  };
  return map[role] || role;
}

// Roles allowed to run integration syncs and see IT-only reports. Defined here
// so the nav, the route guard, and the pages can't drift apart.
export const IT_ROLES = ['it_admin', 'it_staff'];

export function isITRole(role) {
  return IT_ROLES.includes(role);
}
